/**
 * The location page's Jobs section.
 *
 * Lifted verbatim out of `sites/trade-panel.tsx` (#1011) as part of the
 * pure-move commit: no behaviour changed here, so the diff is a rename
 * and the only question a reviewer has to answer is "did anything
 * change?". The IA flip that consumes it is the next commit.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dateSortValue } from "@/lib/table-sort";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { DataTable, openRowColumn, useSortedRows, type Column } from "@/components/ui/data-table";
import { cn, formatDate, statusColor } from "@/lib/utils";
import { Briefcase, Plus } from "lucide-react";
import { CollapsibleSection, ShowMoreFooter } from "./section-chrome";
import { DEFAULT_VISIBLE_ROWS, type JobRow, type SiteContext } from "./types";

/**
 * `tradeId` null = every job at this location, which is what the Jobs TAB
 * shows by default. The trade filter narrows it; it no longer defines it.
 */
export function JobsSection({
    site,
    tradeId,
    tradeCode,
}: {
    site: SiteContext;
    tradeId: string | null;
    tradeCode: string | null;
}) {
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();
    const q = useQuery({
        queryKey: ["site-trade-jobs", site.siteId, tradeId],
        queryFn: () => {
            const p = new URLSearchParams({ siteId: site.siteId, limit: "200" });
            if (tradeId) p.set("tradeId", tradeId);
            return api.get<{ jobs: JobRow[]; total: number }>(`/api/jobs?${p.toString()}`);
        },
        enabled: !!site.siteId,
    });

    const rows = q.data?.jobs ?? [];
    const total = q.data?.total ?? 0;

    const columns: Column<JobRow>[] = [
        {
            key: "id",
            header: "Job",
            sortable: true,
            sortAccessor: (r) => r.display_number ?? r.id.slice(0, 8),
            render: (r) => (
                <Link
                    to={`/jobs/${r.id}`}
                    className="font-mono text-xs text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    #{r.display_number ?? r.id.slice(0, 8)}
                </Link>
            ),
        },
        { key: "title", header: "Title", sortable: true, render: (r) => r.title ?? "—" },
        {
            key: "status",
            header: "Status",
            sortable: true,
            render: (r) => (
                <Badge variant="outline" className={statusColor(r.status)}>
                    {r.status?.replace(/_/g, " ")}
                </Badge>
            ),
        },
        {
            key: "scheduled_start",
            header: "Scheduled",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.scheduled_start),
            render: (r) => (
                <span className="text-xs text-foreground/80">
                    {r.scheduled_start ? formatDate(r.scheduled_start) : "—"}
                </span>
            ),
        },
        {
            // The disagreement nothing on the page used to surface: this
            // location reads 12 devices, its March job was titled
            // "(11) Annual Test", and it carried 9 device rows. Three
            // numbers, three screens, no way to notice. The count sits
            // next to the title now, and reads "none" rather than "0" so
            // an empty job looks like the problem it is.
            key: "asset_count",
            header: "Devices",
            sortable: true,
            firstSortDir: "desc",
            render: (r) =>
                r.asset_count > 0 ? (
                    <span className="text-xs tabular-nums">{r.asset_count}</span>
                ) : (
                    <span className="text-xs font-medium text-warning">none</span>
                ),
        },
        {
            key: "assigned_to_name",
            header: "Assigned",
            sortable: true,
            render: (r) => <span className="text-xs">{r.assigned_to_name ?? "—"}</span>,
        },
        openRowColumn<JobRow>(),
    ];

    // Sort every row, THEN cut to the preview, so a sort reaches past the
    // first few rows. Remembered per user.
    const { sorted, controlledSort } = useSortedRows(rows, columns, { sortId: "site.jobs" });
    const visibleRows = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE_ROWS);

    return (
        <CollapsibleSection
            // Same reason as Tests: this is the whole Jobs tab now, not one
            // of four cards under a KPI strip.
            defaultOpen
            title="Jobs"
            icon={<Briefcase className="h-4 w-4 text-foreground/70" />}
            count={total}
            action={
                <Link
                    to={
                        tradeId
                            ? `/jobs/new?customerId=${site.customerId}&siteId=${site.siteId}&tradeId=${tradeId}`
                            : `/jobs/new?customerId=${site.customerId}&siteId=${site.siteId}`
                    }
                    className={cn(buttonVariants({ size: "sm", variant: "outline" }))}
                >
                    <Plus className="h-4 w-4 mr-1" /> New job
                </Link>
            }
        >
            <DataTable
                columns={columns}
                data={visibleRows}
                keyField="id"
                onRowClick={(r) => navigate(`/jobs/${r.id}`)}
                controlledSort={controlledSort}
                loading={q.isLoading}
                emptyMessage={`No ${tradeCode === "backflow" ? "backflow " : tradeCode === "fire_ext" ? "fire safety " : ""}jobs at this location yet.`}
            />
            <ShowMoreFooter total={total} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
        </CollapsibleSection>
    );
}

// ── Attachments ──────────────────────────────────────────────────────
